export type DocLocale = 'en' | 'fr' | 'es' | 'de';

export type LocalizedText = Record<DocLocale, string>;

export type DocButton = {
  id: string;
  label: LocalizedText;
  action: LocalizedText;
  outcome: LocalizedText;
};

export type WorkspaceDoc = {
  id: string;
  family: 'functional' | 'technical';
  section: LocalizedText;
  title: LocalizedText;
  audience: LocalizedText;
  route?: string;
  objective: LocalizedText;
  steps: LocalizedText[];
  buttons: DocButton[];
  keywords: string[];
};

const t = (value: string): LocalizedText => ({
  en: value,
  fr: value,
  es: value,
  de: value,
});

export const workspaceDocArchitecture = {
  title: t('Workspace Documentation Center'),
  subtitle: t(
    'Two complete manuals: Functional operations and Technical architecture. Content is organized by section, searchable by keyword, and translation-ready.',
  ),
  families: [
    {
      id: 'functional',
      title: t('Functional documentation'),
      description: t(
        'Explains what each workspace page does, who uses it, and every key action/button with expected business outcome.',
      ),
    },
    {
      id: 'technical',
      title: t('Technical documentation'),
      description: t(
        'Explains data flow, auth/session design, API boundaries, security controls, and operational concerns for engineering teams.',
      ),
    },
  ],
};

export const workspaceDocs: WorkspaceDoc[] = [
  {
    id: 'functional-overview',
    family: 'functional',
    section: t('General'),
    title: t('Workspace navigation and global controls'),
    audience: t('Admins, employees, shipping agents'),
    route: '/workspace',
    objective: t(
      'Provide predictable navigation, role-aware visibility, search shortcuts, and common controls across all workspace modules.',
    ),
    steps: [
      t('Open the left sidebar and expand module groups (Users, Products, Finance, Reports, Orders, Settings).'),
      t('Use global top navigation search to jump directly to workspace pages available to your permissions.'),
      t('Use language and currency controls from the header; changes persist in user preferences.'),
      t('Open profile/security controls from account area to maintain password, passkeys, and recovery methods.'),
    ],
    buttons: [
      {
        id: 'sidebar-group-toggle',
        label: t('Sidebar group toggle'),
        action: t('Expand/collapse a navigation group.'),
        outcome: t('Reduces clutter and keeps operator focus on active workflow domain.'),
      },
      {
        id: 'workspace-search',
        label: t('Workspace search'),
        action: t('Find and open workspace screens by name.'),
        outcome: t('Faster navigation and lower training time for staff.'),
      },
    ],
    keywords: ['sidebar', 'navigation', 'search', 'language', 'currency', 'workspace'],
  },
  {
    id: 'functional-mobile-navigation-quick-guide',
    family: 'functional',
    section: t('General'),
    title: t('Mobile workspace navigation quick guide'),
    audience: t('All workspace users on phones/tablets'),
    route: '/workspace',
    objective: t(
      'Provide a short, operator-friendly navigation path to move across workspace modules quickly on mobile screens.',
    ),
    steps: [
      t('Tap the top workspace menu trigger (current section label) to open the mobile navigation panel.'),
      t('Expand a navigation group (Users, Products, Finance, Orders, Marketing, etc.).'),
      t('Tap the destination page (for example Orders, Payments, Customers, or Inventory).'),
      t('Use header search to jump to a page name when you want faster access.'),
      t('If a page is missing from the menu, request the required permission from an admin.'),
    ],
    buttons: [
      {
        id: 'mobile-nav-open',
        label: t('Open workspace menu'),
        action: t('Opens the mobile workspace navigation panel from the header trigger.'),
        outcome: t('User can switch modules without scrolling through the current page content.'),
      },
      {
        id: 'mobile-nav-group-toggle',
        label: t('Group toggle'),
        action: t('Expands or collapses one navigation group in mobile menu.'),
        outcome: t('Keeps menu readable while exposing only the relevant pages.'),
      },
      {
        id: 'mobile-nav-search',
        label: t('Header search'),
        action: t('Searches accessible workspace pages by name/path.'),
        outcome: t('Directly navigates to target module when selected from results.'),
      },
    ],
    keywords: ['mobile', 'navigation', 'workspace menu', 'orders', 'search', 'permissions', 'quick guide'],
  },
  {
    id: 'functional-users',
    family: 'functional',
    section: t('Users'),
    title: t('Customers, employees, roles, and suppliers'),
    audience: t('Admins and authorized managers'),
    route: '/workspace/customers',
    objective: t(
      'Manage user lifecycle, assign permissions, and maintain supplier records used by purchase and inventory modules.',
    ),
    steps: [
      t('Use Customers page filters (search, phone, country, city, date range) to locate customer profiles.'),
      t('Open customer detail to inspect profile, order history, route status, and account metadata.'),
      t('Use Employees page to invite staff accounts and map role assignments.'),
      t('Use Roles page to enforce least-privilege RBAC by assigning only required actions.'),
      t('Use Suppliers page to keep purchasing contacts and supplier data clean.'),
    ],
    buttons: [
      {
        id: 'customers-filters-apply',
        label: t('Apply filters'),
        action: t('Applies current customer filter set to listing query.'),
        outcome: t('Returns scoped customer dataset for support and operations tasks.'),
      },
      {
        id: 'employees-send-invite',
        label: t('Send invite'),
        action: t('Creates employee account and dispatches invite email.'),
        outcome: t('Staff can complete invite, set password, and join workspace securely.'),
      },
      {
        id: 'roles-save-permissions',
        label: t('Save permissions'),
        action: t('Persists role permission changes.'),
        outcome: t('Access rights are updated for users assigned to this role.'),
      },
      {
        id: 'suppliers-save',
        label: t('Save supplier'),
        action: t('Creates or updates supplier record.'),
        outcome: t('Supplier becomes available in purchase and purchase-order forms.'),
      },
    ],
    keywords: ['customers', 'employees', 'roles', 'rbac', 'suppliers', 'invite', 'status'],
  },
  {
    id: 'functional-catalog',
    family: 'functional',
    section: t('Products'),
    title: t('Catalog, categories, inventory, purchases, and losses'),
    audience: t('Operations and procurement teams'),
    route: '/workspace/products',
    objective: t(
      'Maintain sellable catalog quality while keeping stock, purchasing, and loss accounting synchronized.',
    ),
    steps: [
      t('Create products with pricing, base units, optional sale price, and visual assets.'),
      t('Organize products with categories for storefront discoverability.'),
      t('Register purchases and lot metadata to increase stock on hand.'),
      t('Track stock lots and expiry windows to reduce waste and detect risks.'),
      t('Record losses (expired, damaged, theft, other) to keep inventory and finance consistent.'),
    ],
    buttons: [
      {
        id: 'products-create',
        label: t('Create product'),
        action: t('Creates a new product in catalog.'),
        outcome: t('Product becomes available for selling and stock operations.'),
      },
      {
        id: 'inventory-save-purchase',
        label: t('Save purchase'),
        action: t('Registers purchase line items and lot details.'),
        outcome: t('Stock levels increase and purchase history is auditable.'),
      },
      {
        id: 'losses-record',
        label: t('Record loss'),
        action: t('Submits a loss event with reason and quantity.'),
        outcome: t('Stock decreases and budget impact is logged.'),
      },
    ],
    keywords: ['products', 'categories', 'inventory', 'lots', 'purchases', 'losses'],
  },
  {
    id: 'functional-products-page-buttons',
    family: 'functional',
    section: t('Products'),
    title: t('Products page — complete button catalog'),
    audience: t('Admins and operators with PRODUCT permissions'),
    route: '/workspace/products',
    objective: t(
      'Provide a complete, button-level reference for product creation, editing, activation control, image upload, and pagination.',
    ),
    steps: [
      t('Use search to filter by name or SKU; results update after debounce.'),
      t('Open the product form to create or edit a product.'),
      t('Upload an image, set base unit, category, and price, then save.'),
      t('Use Enable/Disable to control storefront visibility and operational availability.'),
      t('Use Edit to load an existing product into the form for updates.'),
      t('Use pagination controls to navigate across product pages.'),
    ],
    buttons: [
      {
        id: 'products-clear-search',
        label: t('Clear'),
        action: t('Clears the product search input.'),
        outcome: t('Reloads product listing with the default (unfiltered) query.'),
      },
      {
        id: 'products-toggle-form',
        label: t('New product / Hide form'),
        action: t('Shows or hides the create/edit product form.'),
        outcome: t('Operators can add new products or focus on the table view.'),
      },
      {
        id: 'products-upload-image',
        label: t('Upload image (file input)'),
        action: t('Uploads an image file to the server and sets product image URL.'),
        outcome: t('Product preview image becomes available for storefront and workspace.'),
      },
      {
        id: 'products-save',
        label: t('Create product / Update product'),
        action: t('Saves product data via API (create or update depending on edit mode).'),
        outcome: t('Product is persisted and listing refreshes to reflect changes.'),
      },
      {
        id: 'products-cancel-form',
        label: t('Cancel'),
        action: t('Closes the product form and exits edit/create mode.'),
        outcome: t('No further edits are applied until form is reopened.'),
      },
      {
        id: 'products-enable-disable',
        label: t('Enable / Disable'),
        action: t('Toggles product active status.'),
        outcome: t('Product is activated or disabled in catalog; list refreshes.'),
      },
      {
        id: 'products-edit',
        label: t('Edit'),
        action: t('Loads selected product into the form for editing.'),
        outcome: t('Form fields are pre-filled; saving updates that product record.'),
      },
      {
        id: 'products-pagination-prev',
        label: t('Prev'),
        action: t('Loads the previous page of products.'),
        outcome: t('Operators can browse older results efficiently.'),
      },
      {
        id: 'products-pagination-next',
        label: t('Next'),
        action: t('Loads the next page of products.'),
        outcome: t('Operators can browse further results efficiently.'),
      },
    ],
    keywords: [
      'products',
      'catalog',
      'create',
      'update',
      'enable',
      'disable',
      'image upload',
      'pagination',
      'buttons',
    ],
  },
  {
    id: 'functional-categories-page-buttons',
    family: 'functional',
    section: t('Products'),
    title: t('Categories page — complete button catalog'),
    audience: t('Admins and operators with CATEGORY permissions'),
    route: '/workspace/categories',
    objective: t(
      'Provide a complete, button-level reference for category creation, editing, activation control, deletion, search, and pagination.',
    ),
    steps: [
      t('Use quick search to filter categories by name, slug, or description.'),
      t('Use the form to create a new category or update an existing one.'),
      t('Use Activate/Deactivate to control category visibility.'),
      t('Use Delete to remove a category (confirmation required).'),
      t('Use pagination to browse the category list.'),
    ],
    buttons: [
      {
        id: 'categories-search-clear',
        label: t('Clear'),
        action: t('Clears the category search query.'),
        outcome: t('Restores the full category list and resets paging.'),
      },
      {
        id: 'categories-form-clear',
        label: t('Clear (form)'),
        action: t('Resets form to “new category” mode and clears editing state.'),
        outcome: t('Form switches back to creation mode without changing data.'),
      },
      {
        id: 'categories-form-submit',
        label: t('Create / Update'),
        action: t('Creates a new category or updates the currently edited category.'),
        outcome: t('Category list refreshes; new/updated category is persisted.'),
      },
      {
        id: 'categories-add-shortcut',
        label: t('+ Add category'),
        action: t('Resets form, clears search, and returns list to first page.'),
        outcome: t('Operator can create a new category quickly from the list view.'),
      },
      {
        id: 'categories-toggle-active',
        label: t('Activate / Deactivate'),
        action: t('Toggles category active status.'),
        outcome: t('Category becomes active or inactive and list refreshes.'),
      },
      {
        id: 'categories-edit',
        label: t('Edit'),
        action: t('Loads selected category into the form for editing.'),
        outcome: t('Form fields are pre-filled; submitting updates the category.'),
      },
      {
        id: 'categories-delete',
        label: t('Delete'),
        action: t('Deletes a category after confirmation.'),
        outcome: t('Category is removed; failure occurs if constrained by existing products.'),
      },
      {
        id: 'categories-pagination-prev',
        label: t('Prev'),
        action: t('Moves to previous page of categories in the list panel.'),
        outcome: t('Earlier categories are shown.'),
      },
      {
        id: 'categories-pagination-next',
        label: t('Next'),
        action: t('Moves to next page of categories in the list panel.'),
        outcome: t('More categories are shown.'),
      },
    ],
    keywords: [
      'categories',
      'create',
      'update',
      'activate',
      'deactivate',
      'delete',
      'search',
      'pagination',
      'buttons',
    ],
  },
  {
    id: 'functional-inventory-page-buttons',
    family: 'functional',
    section: t('Products'),
    title: t('Inventory page — complete button catalog'),
    audience: t('Operators with STOCK_LOT and PURCHASE permissions'),
    route: '/workspace/inventory',
    objective: t(
      'Provide a complete, button-level reference for inventory stock views, lots management, expiry monitoring, sale price controls, purchase creation, and purchase history expansion.',
    ),
    steps: [
      t('Stock view: type in live search to filter products by name or SKU.'),
      t('Use “Expiring soon” to open the expiry modal and review risk by product.'),
      t('Open category “Details” to see product rows and actions (sale price and view lots).'),
      t('Open “View lots” to manage lot expiry dates and clear expiry when needed.'),
      t('Purchases view: open new purchase form, add item lines, then save purchase.'),
      t('Use purchase date groups and per-purchase expand/collapse controls to inspect item lines.'),
    ],
    buttons: [
      {
        id: 'inventory-expiring-soon',
        label: t('Expiring soon (Xd)'),
        action: t('Opens modal listing products with lots expiring within configured days.'),
        outcome: t('Operators can prioritize stock rotation and prevent waste.'),
      },
      {
        id: 'inventory-category-details-toggle',
        label: t('Details / Hide details'),
        action: t('Expands or collapses a category section.'),
        outcome: t('Shows product rows and action controls per category.'),
      },
      {
        id: 'inventory-sale-price-set',
        label: t('Set (sale price)'),
        action: t('Enters edit mode for sale price for a product row.'),
        outcome: t('Operator can input a new sale price and save it.'),
      },
      {
        id: 'inventory-sale-price-save',
        label: t('Save (sale price)'),
        action: t('Saves sale price via API for selected product.'),
        outcome: t('Sale price is persisted and stock table refreshes.'),
      },
      {
        id: 'inventory-sale-price-cancel',
        label: t('Cancel (sale price)'),
        action: t('Exits sale price edit mode without saving.'),
        outcome: t('No changes are applied.'),
      },
      {
        id: 'inventory-view-lots',
        label: t('View lots'),
        action: t('Opens lots modal for selected product.'),
        outcome: t('Operator can view lots and edit expiry dates.'),
      },
      {
        id: 'inventory-lots-close',
        label: t('× (close lots modal)'),
        action: t('Closes the lots modal.'),
        outcome: t('Returns to stock view.'),
      },
      {
        id: 'inventory-lot-expiry-edit',
        label: t('Edit (lot expiry)'),
        action: t('Enters expiry-date edit mode for selected lot.'),
        outcome: t('Operator can set or clear expiry date and save.'),
      },
      {
        id: 'inventory-lot-expiry-save',
        label: t('Save (lot expiry)'),
        action: t('Saves edited lot expiry date via API.'),
        outcome: t('Lot expiry updates and modal refreshes.'),
      },
      {
        id: 'inventory-lot-expiry-cancel',
        label: t('Cancel (lot expiry)'),
        action: t('Exits expiry edit mode without saving.'),
        outcome: t('No changes are applied.'),
      },
      {
        id: 'inventory-lot-expiry-clear',
        label: t('Clear (lot expiry)'),
        action: t('Clears expiry date for the lot.'),
        outcome: t('Expiry date becomes empty (null) and modal refreshes.'),
      },
      {
        id: 'inventory-stock-pagination-prev',
        label: t('Prev (stock)'),
        action: t('Loads previous page of stock summary.'),
        outcome: t('Operator navigates across stock pages.'),
      },
      {
        id: 'inventory-stock-pagination-next',
        label: t('Next (stock)'),
        action: t('Loads next page of stock summary.'),
        outcome: t('Operator navigates across stock pages.'),
      },
      {
        id: 'inventory-purchases-toggle-form',
        label: t('New purchase / Hide form'),
        action: t('Shows or hides the purchase creation form.'),
        outcome: t('Operator can record purchases and create stock lots.'),
      },
      {
        id: 'inventory-purchases-search',
        label: t('Search (purchases)'),
        action: t('Runs purchase history query with current filters.'),
        outcome: t('Purchase listing refreshes according to filters.'),
      },
      {
        id: 'inventory-purchases-add-item',
        label: t('Add item'),
        action: t('Adds a purchase line to the pending purchase.'),
        outcome: t('Line appears in items table and will be saved on submission.'),
      },
      {
        id: 'inventory-purchases-remove-line',
        label: t('Remove (line)'),
        action: t('Removes a pending purchase line.'),
        outcome: t('Line is removed from the pending purchase set.'),
      },
      {
        id: 'inventory-purchases-cancel',
        label: t('Cancel (purchase form)'),
        action: t('Closes purchase form and clears draft line items.'),
        outcome: t('No purchase is created.'),
      },
      {
        id: 'inventory-purchases-save',
        label: t('Save purchase'),
        action: t('Creates purchase and lots via API.'),
        outcome: t('Stock increases; purchase appears in history; errors indicate missing budget period or validation issues.'),
      },
      {
        id: 'inventory-purchase-group-toggle',
        label: t('Purchase date group toggle'),
        action: t('Expands/collapses purchases for a given date.'),
        outcome: t('Operator can drill down into purchases by day.'),
      },
      {
        id: 'inventory-purchase-expand',
        label: t('Expand / Collapse (purchase)'),
        action: t('Expands a single purchase to show all item lines.'),
        outcome: t('Operator can audit purchase contents and expiry/batch metadata.'),
      },
      {
        id: 'inventory-expiry-modal-close',
        label: t('× (close expiring soon modal)'),
        action: t('Closes expiring soon modal.'),
        outcome: t('Returns to inventory stock view.'),
      },
    ],
    keywords: [
      'inventory',
      'stock',
      'lots',
      'expiry',
      'sale price',
      'purchases',
      'add item',
      'remove',
      'expand',
      'buttons',
    ],
  },
  {
    id: 'functional-orders',
    family: 'functional',
    section: t('Orders'),
    title: t('Orders, promotions, delivery batches, feedback'),
    audience: t('Order management and support teams'),
    route: '/workspace/orders',
    objective: t(
      'Control order lifecycle, apply promotions, execute dispatch batches, and process customer feedback.',
    ),
    steps: [
      t('Review orders and transition through allowed statuses.'),
      t('Create promotions with optional customer restrictions and limits.'),
      t('Assign paid orders to delivery batches by area and dispatch windows.'),
      t('Close and dispatch batches, then monitor background progress.'),
      t('Use feedback inbox for incidents and complaint workflows.'),
    ],
    buttons: [
      {
        id: 'orders-status-update',
        label: t('Order status actions'),
        action: t('Updates order progression state.'),
        outcome: t('Operations, reporting, and customer visibility stay synchronized.'),
      },
      {
        id: 'promotions-create',
        label: t('Create promotion'),
        action: t('Creates discount code policy and constraints.'),
        outcome: t('Promotion is enforceable at checkout with anti-abuse restrictions.'),
      },
      {
        id: 'delivery-batches-close-dispatch',
        label: t('Close batch & ship orders'),
        action: t('Queues background shipment status transitions for eligible orders.'),
        outcome: t('Batch closes and orders move to shipped state safely at scale.'),
      },
    ],
    keywords: ['orders', 'promotions', 'delivery', 'batches', 'feedback', 'incidents'],
  },
  {
    id: 'functional-finance',
    family: 'functional',
    section: t('Finance'),
    title: t('Finance dashboard, reconciliation, payments, budgets, reports'),
    audience: t('Finance and admin stakeholders'),
    route: '/workspace/finance',
    objective: t(
      'Deliver decision-ready financial visibility by linking payment inflows, budget movements, and reconciliation events.',
    ),
    steps: [
      t('Use finance dashboard for high-level status and trend monitoring.'),
      t('Review payment records with status and card-safe metadata.'),
      t('Run bank reconciliation against payout timelines and deposit confirmations.'),
      t('Manage budget periods and add adjustments/expenses as needed.'),
      t('Generate PDF reports for selected periods and sections.'),
    ],
    buttons: [
      {
        id: 'payments-view',
        label: t('Details'),
        action: t('Opens full payment record.'),
        outcome: t('Investigations can correlate order, payment intent, and card-safe fields.'),
      },
      {
        id: 'budget-create',
        label: t('Create period'),
        action: t('Starts a new budget period.'),
        outcome: t('Expenses and operational deductions can be tracked by time window.'),
      },
      {
        id: 'reports-download',
        label: t('Download PDF'),
        action: t('Builds printable report file.'),
        outcome: t('Finance outputs can be archived or shared with stakeholders.'),
      },
    ],
    keywords: ['finance', 'payments', 'budget', 'reports', 'reconciliation', 'profit'],
  },
  {
    id: 'functional-orders-page-buttons',
    family: 'functional',
    section: t('Orders'),
    title: t('Orders page — complete button catalog'),
    audience: t('Operators with ORDER, INCIDENT, RESHIP, and PAYMENT permissions'),
    route: '/workspace/orders',
    objective: t(
      'Provide a complete, button-level reference for order filtering, state transitions, incident workflows, batch assignment, stripe verification, and pagination.',
    ),
    steps: [
      t('Choose a scope tab (Processing, Incidents, Conflict resolution, Archive, All) before actioning rows.'),
      t('Use search/status/country/date filters and quick stat chips to narrow target orders.'),
      t('Open details, apply incident/resolution actions, and perform status transitions only when enabled.'),
      t('For card orders in PENDING, verify Stripe before setting paid to preserve reconciliation integrity.'),
      t('Assign PAID orders to delivery batches where delivery speed matches the selected batch.'),
    ],
    buttons: [
      { id: 'orders-view-tab', label: t('View tab'), action: t('Switches order list scope.'), outcome: t('Operator sees only the selected operational queue.') },
      { id: 'orders-clear-filters', label: t('Clear'), action: t('Resets search, country, and date filters.'), outcome: t('Returns to broader order list; status chip may remain if not cleared separately.') },
      { id: 'orders-by-status-chip', label: t('Status chip'), action: t('Applies status filter and switches to All view.'), outcome: t('Fast drill-down to one lifecycle stage for triage.') },
      { id: 'orders-by-country-chip', label: t('Country chip'), action: t('Applies country filter.'), outcome: t('List narrows to country-specific operations and SLA review.') },
      { id: 'orders-view-details', label: t('View details / Hide details'), action: t('Expands or collapses rich order details panel.'), outcome: t('Customer, incident, losses, and event timeline become visible for decisions.') },
      { id: 'orders-archive-expand', label: t('Expand / Collapse (archived row)'), action: t('Expands closed/cancelled row body on demand.'), outcome: t('Archive remains compact while preserving audit access.') },
      {
        id: 'orders-mark-unreachable',
        label: t('Mark unreachable'),
        action: t('Opens incident with UNREACHABLE reason.'),
        outcome: t(
          'Order moves into incident flow. If it was on a delivery batch, it is removed from the batch automatically. Failures return API messages for policy/validation issues.',
        ),
      },
      {
        id: 'orders-mark-damaged',
        label: t('Mark damaged'),
        action: t('Opens incident with DAMAGED reason.'),
        outcome: t(
          'Incident state starts (investigation/resolution next). If the order was on a delivery batch, it is removed from the batch automatically.',
        ),
      },
      { id: 'orders-set-investigating', label: t('Set investigating'), action: t('Transitions incident order to INVESTIGATING.'), outcome: t('Resolution controls become available to authorized roles.') },
      { id: 'orders-set-fault-employee', label: t('Set fault employee'), action: t('Opens modal to confirm kept quantities per line.'), outcome: t('Resolution records loss/reship basis; invalid quantities are blocked before submit.') },
      { id: 'orders-set-fault-customer', label: t('Set fault customer'), action: t('Resolves incident as customer fault.'), outcome: t('Order follows customer-fault closure path and updates audit trail.') },
      { id: 'orders-reship', label: t('Reship'), action: t('Triggers reship action for eligible statuses.'), outcome: t('Order moves through reship lifecycle; unavailable when transition rules disallow it.') },
      { id: 'orders-verify-stripe', label: t('Verify in Stripe & mark paid'), action: t('Validates PaymentIntent against order and updates status.'), outcome: t('Order becomes paid only after external verification; mismatch/failure returns clear API error.') },
      { id: 'orders-manual-set-status', label: t('Set {status}'), action: t('Attempts direct transition to chosen status.'), outcome: t('Button is disabled when transition is illegal, busy, or manual PAID is blocked for Stripe orders.') },
      {
        id: 'orders-batch-assign',
        label: t('Assign to batch'),
        action: t('Assigns a first-shipment (PAID) or reship (RESHIP_PENDING) order to an open batch.'),
        outcome: t(
          'Batch link appears on row. Dispatch moves PAID orders to Shipped and RESHIP_PENDING to Reshipped. Errors return API reasons such as speed or status mismatch.',
        ),
      },
      { id: 'orders-batch-unassign', label: t('Unassign'), action: t('Removes current batch assignment.'), outcome: t('Order returns to unbatched state and can be reassigned.') },
      { id: 'orders-fault-modal-cancel', label: t('Cancel (fault modal)'), action: t('Closes employee-fault modal without saving.'), outcome: t('No resolution is applied.') },
      { id: 'orders-fault-modal-confirm', label: t('Confirm fault (employee)'), action: t('Submits employee-fault resolution with quantity adjustments.'), outcome: t('Resolution is recorded and order flow advances when validation passes.') },
      { id: 'orders-pagination-prev', label: t('Prev'), action: t('Loads previous visible page.'), outcome: t('Moves backward through filtered order subset.') },
      { id: 'orders-pagination-next', label: t('Next'), action: t('Loads next visible page.'), outcome: t('Moves forward through filtered order subset.') },
    ],
    keywords: ['orders', 'incidents', 'resolution', 'stripe verify', 'status transitions', 'reship', 'batch assignment', 'pagination', 'buttons'],
  },
  {
    id: 'functional-delivery-batches-page-buttons',
    family: 'functional',
    section: t('Orders'),
    title: t('Delivery batches page — complete button catalog'),
    audience: t('Dispatch operators with DELIVERY_BATCH permissions'),
    route: '/workspace/delivery-batches',
    objective: t(
      'Provide a complete, button-level reference for batch creation, filtering, editing, order assignment, dispatch close, and removal constraints.',
    ),
    steps: [
      t('Filter the left batch list by text, status, speed, country, and city to select a batch.'),
      t('Create new batch from collapsible form with area, window, and speed metadata.'),
      t('Open batch settings to edit metadata and save changes.'),
      t('Search eligible orders, select rows/groups, assign, and remove as needed while batch is OPEN.'),
      t('Use Close dispatch to queue shipment transition job and monitor progress/errors.'),
    ],
    buttons: [
      { id: 'delivery-batches-toggle-create', label: t('Create batch panel toggle'), action: t('Expands or collapses create form.'), outcome: t('Operator can open batch intake without leaving list context.') },
      { id: 'delivery-batches-save-create', label: t('Save (create batch)'), action: t('Creates batch with entered metadata.'), outcome: t('Batch is persisted and selected; validation errors return API message.') },
      { id: 'delivery-batches-select-row', label: t('Batch row'), action: t('Loads selected batch details.'), outcome: t('Right panel updates with settings, order list, and dispatch actions.') },
      { id: 'delivery-batches-edit-toggle', label: t('Edit batch / Cancel edit'), action: t('Shows or hides editable batch settings.'), outcome: t('Metadata can be updated only when user has update permission.') },
      { id: 'delivery-batches-save-edit', label: t('Save batch changes'), action: t('Persists edited batch metadata.'), outcome: t('Updated values refresh in list/detail; API validation failures are surfaced.') },
      { id: 'delivery-batches-close-dispatch', label: t('Close & dispatch'), action: t('Confirms and starts background close-and-ship job.'), outcome: t('Batch enters dispatched/closed flow; progress and partial failures are displayed.') },
      { id: 'delivery-batches-delete', label: t('Delete batch'), action: t('Deletes selected batch after confirmation.'), outcome: t('Allowed only when batch is OPEN and has zero orders; otherwise disabled.') },
      { id: 'delivery-batches-select-all-results', label: t('Select all results'), action: t('Selects all currently assignable search matches.'), outcome: t('Bulk assignment set is prepared quickly for dispatch planner.') },
      { id: 'delivery-batches-clear-selection', label: t('Clear selection'), action: t('Removes all selected order chips.'), outcome: t('No orders will be assigned until reselection.') },
      { id: 'delivery-batches-select-all-area', label: t('Select all in area'), action: t('Selects all orders in one city/country group.'), outcome: t('Supports geography-coherent batch building.') },
      { id: 'delivery-batches-selected-chip-remove', label: t('Selected order chip (×)'), action: t('Unselects one chosen order id.'), outcome: t('Order is excluded from next bulk assign call.') },
      { id: 'delivery-batches-assign-selected', label: t('Assign selected'), action: t('Bulk assigns selected orders to current batch.'), outcome: t('Successful rows attach to batch; partial failures show compact per-order error list.') },
      { id: 'delivery-batches-remove-order', label: t('Remove'), action: t('Unassigns one order from batch.'), outcome: t('Order leaves batch and becomes available for reassignment.') },
    ],
    keywords: ['delivery batches', 'dispatch', 'assign bulk', 'close and ship', 'batch edit', 'remove', 'open only constraints', 'buttons'],
  },
  {
    id: 'functional-promotions-page-buttons',
    family: 'functional',
    section: t('Orders'),
    title: t('Promotions page — complete button catalog'),
    audience: t('Marketing and ops users with promotions access'),
    route: '/workspace/promotions',
    objective: t(
      'Provide a complete, button-level reference for promotion creation and deactivation with eligibility controls.',
    ),
    steps: [
      t('Open create form, define code, discount type/value, and optional constraints.'),
      t('Submit promotion to activate immediately.'),
      t('Review list and deactivate promotions when needed.'),
    ],
    buttons: [
      { id: 'promotions-open-form', label: t('Create promotion'), action: t('Toggles promotion create form visibility.'), outcome: t('Operator can create or hide draft form quickly.') },
      { id: 'promotions-save', label: t('Save promotion'), action: t('Creates promotion with current form fields.'), outcome: t('Promotion appears in table; invalid payload returns API message.') },
      { id: 'promotions-cancel', label: t('Cancel'), action: t('Closes form without submission.'), outcome: t('Draft is abandoned for this open session.') },
      { id: 'promotions-deactivate', label: t('Deactivate'), action: t('Confirms then soft-disables active promotion.'), outcome: t('Code remains historical but no longer applies at checkout.') },
    ],
    keywords: ['promotions', 'discount code', 'deactivate', 'eligibility limits', 'restricted customer', 'buttons'],
  },
  {
    id: 'functional-payments-page-buttons',
    family: 'functional',
    section: t('Finance'),
    title: t('Payments page — complete button catalog'),
    audience: t('Finance/support users with PAYMENT read access'),
    route: '/workspace/payments',
    objective: t(
      'Provide a complete, button-level reference for payment filtering, id inspection, detail payload review, and controlled refund operations.',
    ),
    steps: [
      t('Set search/status/date filters and apply query.'),
      t('Expand payment/order ids as needed for copy-safe investigation.'),
      t('Open detail modal for full payment payload review and close when done.'),
      t('Use Refund only when request lifecycle is approved/return received and payment is refundable.'),
    ],
    buttons: [
      { id: 'payments-apply', label: t('Apply'), action: t('Runs payments query using current filters.'), outcome: t('Table refreshes to matching payment set.') },
      { id: 'payments-expand-order-id', label: t('View order id'), action: t('Expands/collapses full order id block.'), outcome: t('Operator can inspect/copy long ids without changing layout permanently.') },
      { id: 'payments-expand-payment-id', label: t('View payment id'), action: t('Expands/collapses full payment id block.'), outcome: t('Stable id visibility for support and reconciliation workflows.') },
      { id: 'payments-view', label: t('View'), action: t('Fetches payment detail endpoint and opens modal.'), outcome: t('Full JSON record is shown; detail fetch failure displays modal error state.') },
      { id: 'payments-refund', label: t('Refund'), action: t('Opens refund modal and submits minor-unit amount for eligible payment rows.'), outcome: t('Refund request is processed; list refreshes and API validation errors are surfaced.') },
      { id: 'payments-close-modal', label: t('Close detail (X)'), action: t('Closes payment detail modal.'), outcome: t('User returns to payments list context.') },
    ],
    keywords: ['payments', 'filters', 'payment id', 'order id', 'detail modal', 'refund', 'reconciliation', 'buttons'],
  },
  {
    id: 'functional-customers-page-buttons',
    family: 'functional',
    section: t('Users'),
    title: t('Customers page — complete button catalog'),
    audience: t('Support and admin users with customer read access'),
    route: '/workspace/customers',
    objective: t(
      'Provide a complete, button-level reference for advanced filters, sorting, paging, and customer detail navigation.',
    ),
    steps: [
      t('Expand filters, compose search/location/date criteria, and apply.'),
      t('Adjust sort and rows-per-page for workload style.'),
      t('Open customer details from row click or explicit action button.'),
    ],
    buttons: [
      { id: 'customers-filters-toggle', label: t('Show filters / Hide filters'), action: t('Expands or collapses filter panel and persists UI state in session storage.'), outcome: t('Operators keep preferred dense/expanded working layout across page revisits.') },
      { id: 'customers-filters-apply-advanced', label: t('Apply filters'), action: t('Commits draft filter fields to URL query state.'), outcome: t('Listing reloads with server-side filtering and page reset to first page.') },
      { id: 'customers-filters-reset', label: t('Reset filters'), action: t('Clears draft and committed filters.'), outcome: t('Returns to default unfiltered listing.') },
      { id: 'customers-sort', label: t('Sort selector'), action: t('Changes sort strategy and resets to page 1.'), outcome: t('Rows reorder by creation date, spend, or order count direction.') },
      { id: 'customers-page-size', label: t('Rows per page'), action: t('Sets page size and resets to page 1.'), outcome: t('Density changes to 10/20/50/100 rows per fetch.') },
      { id: 'customers-open-detail', label: t('Open detail'), action: t('Navigates to customer detail route.'), outcome: t('Support can inspect full profile/order history; return query is preserved for back navigation.') },
      { id: 'customers-pagination-prev', label: t('Prev'), action: t('Moves to previous result page.'), outcome: t('Page index decreases when available.') },
      { id: 'customers-pagination-next', label: t('Next'), action: t('Moves to next result page.'), outcome: t('Page index increases when available.') },
    ],
    keywords: ['customers', 'filters', 'location suggest', 'sort', 'rows per page', 'open detail', 'pagination', 'buttons'],
  },
  {
    id: 'functional-employees-roles-page-buttons',
    family: 'functional',
    section: t('Users'),
    title: t('Employees and roles pages — complete button catalog'),
    audience: t('Admins with employee and RBAC permissions'),
    route: '/workspace/employees',
    objective: t(
      'Provide a complete, button-level reference for employee invite, status control, role assignment modal, role creation, and role permission editing.',
    ),
    steps: [
      t('From Employees page, open invite form, submit invite, and review status feedback.'),
      t('Use Manage roles per employee to assign or remove role memberships.'),
      t('Use Enable/Disable for non-admin employee accounts.'),
      t('From Roles page, create role and open Manage to edit permission groups.'),
      t('In Role permissions page, use group select/clear and save final permission set.'),
    ],
    buttons: [
      { id: 'employees-toggle-invite-form', label: t('Add employee / Hide form'), action: t('Toggles employee invite form visibility.'), outcome: t('Admin can switch between roster view and invite flow quickly.') },
      { id: 'employees-send-invite-deep', label: t('Send invite'), action: t('Creates employee record and sends invite email.'), outcome: t('Employee onboarding begins; duplicate/invalid payload returns API error message.') },
      { id: 'employees-go-roles', label: t('Roles'), action: t('Navigates from Employees page to Roles page.'), outcome: t('Admin can maintain RBAC catalog without menu traversal.') },
      { id: 'employees-manage-roles', label: t('Manage roles'), action: t('Opens user-role assignment modal and fetches current role links.'), outcome: t('Modal allows granular membership changes; fetch failures show modal error.') },
      { id: 'employees-toggle-status', label: t('Enable / Disable'), action: t('Toggles employee account status.'), outcome: t('Account becomes active or blocked; self-disable and admin rows are restricted in UI.') },
      { id: 'employees-roles-modal-close', label: t('Close / Cancel (roles modal)'), action: t('Closes role assignment modal without persisting pending checks.'), outcome: t('No role changes are written.') },
      { id: 'employees-roles-modal-save', label: t('Save (roles modal)'), action: t('Persists selected role ids for employee.'), outcome: t('User-role mappings update; backend validation errors are surfaced.') },
      { id: 'roles-new-toggle', label: t('New role / Cancel'), action: t('Toggles role creation form.'), outcome: t('Admin can add role definitions inline.') },
      { id: 'roles-create', label: t('Create role'), action: t('Creates role with name and optional description.'), outcome: t('Role appears in list for later permission mapping.') },
      { id: 'roles-manage', label: t('Manage'), action: t('Navigates to role permission editor.'), outcome: t('Admin can configure permission keys by group.') },
      { id: 'role-perms-group-select-all', label: t('Select all (group)'), action: t('Checks all permissions in one group.'), outcome: t('Speeds full-capability role setup per domain.') },
      { id: 'role-perms-group-clear', label: t('Clear (group)'), action: t('Unchecks all permissions in one group.'), outcome: t('Removes domain capabilities quickly for least-privilege tuning.') },
      { id: 'role-perms-save', label: t('Save'), action: t('Persists selected permission keys for role.'), outcome: t('Role authorization updates globally; system roles remain read-only and save is blocked.') },
      { id: 'role-perms-back', label: t('Back'), action: t('Returns to roles list.'), outcome: t('Admin exits editor without extra navigation steps.') },
    ],
    keywords: ['employees', 'invite', 'enable disable', 'manage roles', 'roles', 'permissions', 'rbac', 'system role readonly', 'buttons'],
  },
  {
    id: 'functional-settings-display-page-buttons',
    family: 'functional',
    section: t('System settings'),
    title: t('Display settings — complete button catalog'),
    audience: t('All workspace users; global controls require admin/allowed roles'),
    route: '/workspace/display',
    objective: t('Explain every control that changes text size and global font family across the application.'),
    steps: [
      t('Review the “Current font size” badge to confirm what is applied right now.'),
      t('Choose whether your account uses the global default or a personal custom size.'),
      t('Admins (or permitted roles) can change the global default size and global font family.'),
    ],
    buttons: [
      { id: 'display-use-global', label: t('Use global'), action: t('Sets your account to follow the global default sizing.'), outcome: t('Your visible size follows global updates; custom size selector is disabled.') },
      { id: 'display-use-custom', label: t('Use custom'), action: t('Sets your account to use a personal sizing preset.'), outcome: t('Your UI uses the selected custom preset regardless of global default changes.') },
      { id: 'display-custom-size-select', label: t('Custom size (select)'), action: t('Chooses your personal preset (Small/Default/Large/Extra large).'), outcome: t('Text density changes for your account; setting is persisted by your profile preferences.') },
      { id: 'display-global-size-select', label: t('Global default (select)'), action: t('Updates the global default sizing preset.'), outcome: t('Applies to users who choose “Use global”; disabled for users without permission.') },
      { id: 'display-font-family-card', label: t('Font family option (card)'), action: t('Sets global font family immediately (admin/allowed only).'), outcome: t('Typography changes across the app for global users; disabled when permission is missing.') },
    ],
    keywords: ['display', 'font size', 'global default', 'custom size', 'font family', 'permissions', 'buttons'],
  },
  {
    id: 'functional-settings-landing-promotion-page-buttons',
    family: 'functional',
    section: t('System settings'),
    title: t('Landing promotion — complete button catalog'),
    audience: t('Admins/operators with UI_SETTING:UPDATE'),
    route: '/workspace/landing-promotion',
    objective: t('Explain every control for the storefront landing promotion hero, including server-side background removal and publish/save behavior.'),
    steps: [
      t('Toggle enabled to decide whether the promotion replaces the home carousel.'),
      t('Edit text, promo code, button label/link, and optional countdown.'),
      t('Upload an image to trigger server background removal; wait for completion before saving.'),
      t('Choose colors via picker and copy hex codes if needed.'),
      t('Save promotion to publish the current configuration.'),
    ],
    buttons: [
      { id: 'landing-promo-enabled', label: t('Show promotion on storefront home (checkbox)'), action: t('Enables/disables promotion on home page.'), outcome: t('When enabled, promotion replaces hero carousel; disabled users cannot change it.') },
      { id: 'landing-promo-clear-countdown', label: t('Clear countdown'), action: t('Removes countdown end time.'), outcome: t('Countdown UI disappears on storefront until an end time is set again.') },
      { id: 'landing-promo-image-file', label: t('Image upload (file input)'), action: t('Uploads image and starts server background removal job.'), outcome: t('Progress panel appears; completion sets processed image URL into the form.') },
      { id: 'landing-promo-image-url', label: t('Image URL (text input)'), action: t('Directly sets image URL/path.'), outcome: t('Preview updates; if invalid/empty, storefront may show no image.') },
      { id: 'landing-promo-image-scale-range', label: t('Storefront image size (slider)'), action: t('Adjusts image scale percentage for layout.'), outcome: t('Image becomes larger/smaller in storefront hero after save.') },
      { id: 'landing-promo-image-scale-number', label: t('Exact % (number input)'), action: t('Sets image scale percentage precisely.'), outcome: t('Same as slider; value is clamped to safe limits on save.') },
      { id: 'landing-promo-dismiss-bg-error', label: t('Dismiss'), action: t('Dismisses background removal failure banner.'), outcome: t('Error banner hides; operator can retry with a new image.') },
      { id: 'landing-promo-color-picker', label: t('Color picker'), action: t('Updates gradient/CTA/text hex color values.'), outcome: t('Hex value updates and will be applied after saving.') },
      { id: 'landing-promo-copy-hex', label: t('Copy'), action: t('Copies current hex color to clipboard.'), outcome: t('Hex is available for reuse in other branding tooling.') },
      { id: 'landing-promo-save', label: t('Save promotion'), action: t('Persists landing promotion configuration.'), outcome: t('Storefront uses the new promotion; save is blocked while background job is busy.') },
    ],
    keywords: ['landing promotion', 'storefront hero', 'promo code', 'background removal', 'image scale', 'color picker', 'save', 'buttons'],
  },
  {
    id: 'functional-settings-recommendations-page-buttons',
    family: 'functional',
    section: t('System settings'),
    title: t('Recommendations — complete button catalog'),
    audience: t('Admins/operators with settings access'),
    route: '/workspace/recommendations',
    objective: t('Explain every control for homepage and cart recommendation behavior.'),
    steps: [
      t('Toggle homepage/cart recommendation switches.'),
      t('Set shelf sizes and maximum shelves.'),
      t('Save settings and confirm success banner.'),
    ],
    buttons: [
      { id: 'recommendations-home-enabled', label: t('Homepage recommendations (checkbox)'), action: t('Enables/disables recommendation shelves on storefront home.'), outcome: t('Homepage shows or hides merchandising rails.') },
      { id: 'recommendations-cart-enabled', label: t('Cart recommendations (checkbox)'), action: t('Enables/disables cart recommendation block.'), outcome: t('Cart shows related/frequently-bought suggestions when enabled.') },
      { id: 'recommendations-save', label: t('Save settings'), action: t('Persists recommendation strategy.'), outcome: t('Backend stores updated values and UI shows success or error banner.') },
    ],
    keywords: ['recommendations', 'homepage shelves', 'cart upsell', 'save settings', 'buttons'],
  },
  {
    id: 'functional-settings-checkout-marketing-page-buttons',
    family: 'functional',
    section: t('System settings'),
    title: t('Checkout marketing — complete button catalog'),
    audience: t('Admins/operators with settings access'),
    route: '/workspace/checkout-marketing',
    objective: t('Explain every control for “checkout pick discount” offer policy.'),
    steps: [
      t('Toggle offer enabled.'),
      t('Set minimum subtotal, “almost there” gap, and discount percent.'),
      t('Save and verify success banner; failures show error banner.'),
    ],
    buttons: [
      { id: 'checkout-marketing-enabled', label: t('Enable offer (checkbox)'), action: t('Turns checkout pick-discount offer on or off.'), outcome: t('Customers are eligible at checkout only when enabled.') },
      { id: 'checkout-marketing-save', label: t('Save'), action: t('Persists checkout marketing settings.'), outcome: t('Backend stores policy; errors indicate validation or server issues.') },
    ],
    keywords: ['checkout marketing', 'discount', 'min subtotal', 'percent off', 'save', 'buttons'],
  },
  {
    id: 'functional-settings-testimonials-page-buttons',
    family: 'functional',
    section: t('System settings'),
    title: t('Testimonials — complete button catalog'),
    audience: t('Admins/operators with settings access'),
    route: '/workspace/testimonials',
    objective: t('Explain every control for curating storefront testimonial carousel from manual entries and customer feedback.'),
    steps: [
      t('Review selected testimonials (max 10).'),
      t('Use arrows to reorder; toggle Active/Hidden for visibility.'),
      t('Add manual testimonial or promote customer feedback into carousel.'),
      t('Refresh inbox to load recent feedback.'),
    ],
    buttons: [
      { id: 'testimonials-move-up', label: t('↑'), action: t('Moves testimonial one position earlier.'), outcome: t('Carousel order changes; save triggers immediately.') },
      { id: 'testimonials-move-down', label: t('↓'), action: t('Moves testimonial one position later.'), outcome: t('Carousel order changes; save triggers immediately.') },
      { id: 'testimonials-toggle-active', label: t('Active / Hidden'), action: t('Toggles whether testimonial is shown on storefront.'), outcome: t('Hidden items are excluded from carousel while still stored.') },
      { id: 'testimonials-add-manual', label: t('Add'), action: t('Adds a manual testimonial to the list and saves.'), outcome: t('New entry appears (up to 10); disabled when already at limit.') },
      { id: 'testimonials-refresh-inbox', label: t('Refresh'), action: t('Reloads feedback inbox list.'), outcome: t('Shows newer feedback items available for promotion.') },
      { id: 'testimonials-promote-feedback', label: t('Add to carousel'), action: t('Promotes a feedback message into carousel list and saves.'), outcome: t('New testimonial is created with CUSTOMER_FEEDBACK source; disabled at max limit.') },
    ],
    keywords: ['testimonials', 'carousel', 'reorder', 'active hidden', 'manual', 'feedback', 'max 10', 'buttons'],
  },
  {
    id: 'functional-settings-site-content-page-buttons',
    family: 'functional',
    section: t('System settings'),
    title: t('Site content — complete button catalog'),
    audience: t('Admins/operators with UI settings access'),
    route: '/workspace/site-content',
    objective: t('Explain every control for updating About, public contact, stats, social links, and legal pages content.'),
    steps: [
      t('Edit content fields for About/stat/social/legal sections.'),
      t('Use public contact suggestion inputs in the correct order (country → city → address).'),
      t('Save content to publish changes.'),
    ],
    buttons: [
      { id: 'site-content-public-country', label: t('Country (suggest input)'), action: t('Sets public contact country and resets city/address/coords.'), outcome: t('City/address suggestions are scoped to the new country.') },
      { id: 'site-content-public-city', label: t('City (suggest input)'), action: t('Sets public contact city and resets address/coords.'), outcome: t('Address suggestions become scoped to the chosen city.') },
      { id: 'site-content-public-address', label: t('Address (suggest input)'), action: t('Sets address label and (when suggestion matches) auto-fills latitude/longitude.'), outcome: t('Storefront can display accurate map/geo; manual overrides remain possible.') },
      { id: 'site-content-save', label: t('Save content'), action: t('Persists site content payload.'), outcome: t('Storefront and public pages reflect updated content; errors return API message.') },
    ],
    keywords: ['site content', 'about', 'legal', 'privacy', 'terms', 'public contact', 'address suggestions', 'save', 'buttons'],
  },
  {
    id: 'functional-settings-hero-page-buttons',
    family: 'functional',
    section: t('System settings'),
    title: t('Hero carousel — complete button catalog'),
    audience: t('Admins/operators with content permissions'),
    route: '/workspace/hero',
    objective: t('Explain every control for managing hero slides: upload, create/edit, activate/deactivate, delete.'),
    steps: [
      t('Use form to create or edit a slide (image + headline required).'),
      t('Upload image file or reuse existing image URL.'),
      t('Edit existing slides from list; toggle active to hide/show; delete with confirmation.'),
    ],
    buttons: [
      { id: 'hero-image-upload', label: t('Image (file input)'), action: t('Selects an image file for upload.'), outcome: t('On save, file is uploaded and stored URL is used for the slide.') },
      { id: 'hero-clear', label: t('Clear'), action: t('Resets form to “new slide” mode and clears draft fields.'), outcome: t('Operator exits edit mode without applying changes.') },
      { id: 'hero-submit', label: t('Add slide / Save'), action: t('Creates or updates a hero slide.'), outcome: t('Slide is persisted; validation failures show error banner; list refreshes.') },
      { id: 'hero-edit', label: t('Edit'), action: t('Loads a slide into the form for editing.'), outcome: t('Form fields prefill and save updates that slide.') },
      { id: 'hero-toggle-active', label: t('Active / Inactive'), action: t('Toggles slide visibility.'), outcome: t('Slide becomes visible/hidden on storefront carousel depending on active flag.') },
      { id: 'hero-delete', label: t('Delete'), action: t('Deletes slide after confirmation.'), outcome: t('Slide is removed; failures surface as error message.') },
    ],
    keywords: ['hero', 'carousel', 'slides', 'upload', 'edit', 'active', 'delete', 'buttons'],
  },
  {
    id: 'functional-settings-currencies-page-buttons',
    family: 'functional',
    section: t('System settings'),
    title: t('Currencies — complete button catalog'),
    audience: t('Admins/operators with currency management access'),
    route: '/workspace/currencies',
    objective: t('Explain every control for adding/removing supported currencies and searching known currency list.'),
    steps: [
      t('Review supported currencies list (USD cannot be removed).'),
      t('Search known currencies by code/name, then add to supported list.'),
      t('Remove supported currency (with confirmation) when no longer needed.'),
    ],
    buttons: [
      { id: 'currencies-search', label: t('Search (input)'), action: t('Filters known currencies list with debounce.'), outcome: t('Known list refreshes to matching codes/names.') },
      { id: 'currencies-add', label: t('Add'), action: t('Adds selected known currency to supported list.'), outcome: t('Currency becomes supported; exchange rates refresh; errors show API message.') },
      { id: 'currencies-remove', label: t('Remove'), action: t('Removes currency from supported list after confirmation.'), outcome: t('Currency is no longer selectable; USD removal is blocked with clear error.') },
    ],
    keywords: ['currencies', 'exchange rates', 'supported', 'known list', 'add', 'remove', 'usd base currency', 'buttons'],
  },
  {
    id: 'functional-settings-delivery-pricing-page-buttons',
    family: 'functional',
    section: t('System settings'),
    title: t('Delivery pricing — complete button catalog'),
    audience: t('Admins/operators with DELIVERY_PRICING:UPDATE or UI_SETTING:UPDATE'),
    route: '/workspace/delivery-pricing',
    objective: t('Explain every control for configuring delivery fees, per-country rules, route sync progress, and store location overrides.'),
    steps: [
      t('Review route sync progress and refresh when needed.'),
      t('Confirm store origin coordinates and source; set/clear override if required.'),
      t('Edit global ETA days and express enabled.'),
      t('Add countries, search list, expand country panels, and edit standard/express fee components.'),
      t('Save pricing and optionally resync all customer routes to recompute delivery estimates.'),
    ],
    buttons: [
      { id: 'delivery-pricing-refresh-progress', label: t('Refresh progress'), action: t('Reloads route recomputation progress snapshot.'), outcome: t('Operator can monitor queue backlog and failures for remediation.') },
      { id: 'delivery-pricing-resync-all', label: t('Resync all customer routes'), action: t('Queues recomputation for all customer routes.'), outcome: t('Background jobs are enqueued; notice shows queued/total/failures.') },
      { id: 'delivery-pricing-edit-public-contact', label: t('Edit public contact'), action: t('Navigates to Site content public contact section.'), outcome: t('Operator can update address used as origin when store source is public contact.') },
      { id: 'delivery-pricing-toggle-override', label: t('Override section'), action: t('Shows/hides latitude/longitude override inputs.'), outcome: t('Operator can set explicit store coordinates if public contact is incorrect.') },
      { id: 'delivery-pricing-clear-override', label: t('Clear override'), action: t('Clears store override after confirmation.'), outcome: t('Origin reverts to public contact (if available) or none.') },
      { id: 'delivery-pricing-set-override', label: t('Set override'), action: t('Saves override coordinates and optional label.'), outcome: t('Origin source becomes override; route computations use new origin.') },
      { id: 'delivery-pricing-express-enabled', label: t('Express enabled (checkbox)'), action: t('Globally enables/disables express speed.'), outcome: t('Express pricing is used only when enabled.') },
      { id: 'delivery-pricing-add-country', label: t('Add country / Add country…'), action: t('Adds a new country rule (from quick button or select).'), outcome: t('New country appears in rules list and can be expanded for editing.') },
      { id: 'delivery-pricing-country-remove', label: t('Remove country (trash)'), action: t('Removes a country rule when allowed.'), outcome: t('US and origin country cannot be removed; otherwise rule disappears from list.') },
      { id: 'delivery-pricing-country-expand', label: t('Country panel toggle'), action: t('Expands/collapses a country rule editor.'), outcome: t('Fee components become editable only when expanded.') },
      { id: 'delivery-pricing-save', label: t('Save'), action: t('Persists pricing config.'), outcome: t('Saved banner appears; validation failures show error and do not apply changes.') },
    ],
    keywords: ['delivery pricing', 'fees', 'per country', 'express', 'route recompute', 'origin override', 'save', 'buttons'],
  },
  {
    id: 'functional-settings-mail-routing-page-buttons',
    family: 'functional',
    section: t('System settings'),
    title: t('Mail routing — complete button catalog'),
    audience: t('Admins/operators with MAIL_ROUTING:UPDATE'),
    route: '/workspace/mail-routing',
    objective: t('Explain every control for configuring “from”, “reply-to”, and “bcc” rules per transactional email purpose.'),
    steps: [
      t('Review list of purposes and fill from/reply-to/bcc for each as needed.'),
      t('Save routing rules; read-only users see warning panel and inputs are disabled.'),
    ],
    buttons: [
      { id: 'mail-routing-from', label: t('From emails (textarea)'), action: t('Sets allowed from addresses for a purpose (one per line or comma-separated).'), outcome: t('Outbound email sender selection uses this list when configured.') },
      { id: 'mail-routing-reply-to', label: t('Reply-to (input)'), action: t('Sets reply-to address for a purpose.'), outcome: t('Replies go to configured inbox when valid.') },
      { id: 'mail-routing-bcc', label: t('BCC emails (textarea)'), action: t('Sets BCC recipients for a purpose.'), outcome: t('Copies are sent for auditing/ops distribution as configured.') },
      { id: 'mail-routing-save', label: t('Save'), action: t('Persists mail routing configuration.'), outcome: t('Saved banner appears and reloaded config matches stored values; save errors show banner.') },
    ],
    keywords: ['mail routing', 'from', 'reply-to', 'bcc', 'purposes', 'transactional email', 'save', 'buttons'],
  },
  {
    id: 'functional-settings-expiry-alerts-page-buttons',
    family: 'functional',
    section: t('System settings'),
    title: t('Expiry alerts — complete button catalog'),
    audience: t('Admins/operators with expiry alert access'),
    route: '/workspace/cron-jobs',
    objective: t('Explain every control for configuring daily expiry alerts, previewing expiring lots, and reviewing alert history with pagination.'),
    steps: [
      t('Configure send time, days window, recipients, and active toggle.'),
      t('Save configuration and verify success message.'),
      t('Load preview for selected days to review lots and remaining quantities.'),
      t('Use history paging to browse previous alert sends.'),
    ],
    buttons: [
      { id: 'expiry-alerts-save', label: t('Save'), action: t('Saves alert configuration.'), outcome: t('Cron settings persist; invalid recipients/time window failures return API error message.') },
      { id: 'expiry-alerts-active', label: t('Alerts active (checkbox)'), action: t('Enables/disables daily alert send.'), outcome: t('When disabled, scheduled emails stop until re-enabled.') },
      { id: 'expiry-alerts-load-preview', label: t('Load preview'), action: t('Fetches preview lots expiring within preview days.'), outcome: t('Preview table populates; empty list means no matches or not loaded yet.') },
      { id: 'expiry-alerts-logs-prev', label: t('Prev (logs)'), action: t('Moves to previous logs page.'), outcome: t('Shows older alert history.') },
      { id: 'expiry-alerts-logs-next', label: t('Next (logs)'), action: t('Moves to next logs page.'), outcome: t('Shows newer/remaining alert history pages.') },
    ],
    keywords: ['expiry alerts', 'cron', 'recipients', 'preview', 'logs', 'pagination', 'inventory expiry', 'buttons'],
  },
  {
    id: 'functional-finance-dashboard-page-buttons',
    family: 'functional',
    section: t('Finance'),
    title: t('Finance dashboard — complete button catalog'),
    audience: t('Finance/admin stakeholders with finance read access'),
    route: '/workspace/finance',
    objective: t('Explain finance portfolio snapshot and per-currency health cards used for daily reconciliation awareness.'),
    steps: [
      t('Open Finance to view snapshot totals across confirmed, paid, and pending exposure.'),
      t('Review per-currency cards and composition bar to identify pending risk and unconfirmed payouts.'),
      t('If values look stale, refresh by reloading the page (this view auto-loads on open).'),
    ],
    buttons: [],
    keywords: ['finance', 'summary', 'exposure', 'collection rate', 'bank confirmation rate', 'dashboard'],
  },
  {
    id: 'functional-finance-reconciliation-page-buttons',
    family: 'functional',
    section: t('Finance'),
    title: t('Bank reconciliation — complete button catalog'),
    audience: t('Finance/admin users with reconciliation access'),
    route: '/workspace/finance/reconciliation',
    objective: t('Explain every control for syncing Stripe payouts, creating bank confirmations, selecting payouts, and linking them to confirmations.'),
    steps: [
      t('Sync payouts to pull latest Stripe payout objects into the system.'),
      t('Record a bank confirmation (deposit) with period and bank amount.'),
      t('Select one confirmation, tick payouts to link, and press Link.'),
      t('Review notices and fix mismatches by adjusting selection or recording a new confirmation.'),
    ],
    buttons: [
      { id: 'recon-sync-payouts', label: t('Sync payouts'), action: t('Calls Stripe sync endpoint to upsert payouts.'), outcome: t('Unlinked payout list refreshes; failures show API error message.') },
      { id: 'recon-payout-select', label: t('Select (checkbox)'), action: t('Adds/removes payout from the current linking set.'), outcome: t('Selected count and amount totals update for reconciliation matching.') },
      { id: 'recon-confirm-deposit', label: t('Confirm deposit'), action: t('Creates a bank confirmation record with currency/period/amount.'), outcome: t('Confirmation appears in list and can be used to link payouts.') },
      { id: 'recon-select-confirmation', label: t('Confirmation row'), action: t('Selects which bank confirmation will receive payout links.'), outcome: t('Link action targets this confirmation id.') },
      { id: 'recon-link', label: t('Link'), action: t('Links selected Stripe payout ids to selected confirmation.'), outcome: t('Selected payouts are removed from “unlinked” list; notice displays linked count; errors show API message.') },
    ],
    keywords: ['bank reconciliation', 'stripe payouts', 'sync', 'confirm deposit', 'link payouts', 'finance', 'buttons'],
  },
  {
    id: 'functional-finance-budget-page-buttons',
    family: 'functional',
    section: t('Finance'),
    title: t('Budget — complete button catalog'),
    audience: t('Finance/admin users with budget access'),
    route: '/workspace/budget',
    objective: t('Explain every control for creating budget periods, activating/closing, and adding adjustments/expenses with audit attribution.'),
    steps: [
      t('Create a new period (name, start/end, initial amount).'),
      t('Activate a draft period when it becomes the operational budget window.'),
      t('Add adjustments (+/-) and other expenses as needed.'),
      t('Close active period to prevent further automated deductions in that window.'),
      t('Use “View detail” to see entries and balances with employee attribution.'),
    ],
    buttons: [
      { id: 'budget-new-toggle', label: t('New period / Cancel'), action: t('Toggles the create-period form.'), outcome: t('Allows creating a new budget period inline without leaving the page.') },
      { id: 'budget-create', label: t('Create'), action: t('Creates a new budget period.'), outcome: t('Period appears in list; failures show error message.') },
      { id: 'budget-view-detail', label: t('View detail / Hide detail'), action: t('Expands/collapses one period’s detailed entries.'), outcome: t('Shows balance, entries table, and entry creation controls.') },
      { id: 'budget-activate', label: t('Activate'), action: t('Transitions a period from DRAFT to ACTIVE.'), outcome: t('Automated deductions and budgeting policy apply to this period.') },
      { id: 'budget-close', label: t('Close'), action: t('Transitions an ACTIVE period to CLOSED.'), outcome: t('Period is frozen from further lifecycle changes via this UI.') },
      { id: 'budget-add-adjustment', label: t('Add adjustment'), action: t('Creates an adjustment entry (+/-) on selected period.'), outcome: t('Balance recalculates; entry appears with employee attribution.') },
      { id: 'budget-add-expense', label: t('Add expense'), action: t('Creates an “other expense” entry on selected period.'), outcome: t('Balance decreases; entry appears with description and attribution.') },
    ],
    keywords: ['budget', 'periods', 'activate', 'close', 'adjustment', 'expense', 'balance', 'entries', 'buttons'],
  },
  {
    id: 'functional-finance-reports-page-buttons',
    family: 'functional',
    section: t('Finance'),
    title: t('Stats & reports — complete button catalog'),
    audience: t('Finance/admin users with report access'),
    route: '/workspace/reports',
    objective: t('Explain every control for selecting budget period sections and generating PDF downloads or email-delivered reports.'),
    steps: [
      t('Select a budget period.'),
      t('Choose which sections to include (summary, entries, purchases-only, losses-only, other expenses).'),
      t('Download PDF or switch to email delivery and send to recipients.'),
    ],
    buttons: [
      { id: 'reports-section-toggle', label: t('Section checkbox'), action: t('Includes/excludes one section from report generation.'), outcome: t('Report content changes; at least one section is required to enable actions.') },
      { id: 'reports-download', label: t('Download PDF'), action: t('Generates report and triggers browser download.'), outcome: t('PDF downloads; failure shows error message.') },
      { id: 'reports-email-open', label: t('Send by email…'), action: t('Opens email delivery options panel.'), outcome: t('Inputs for To/CC/message are revealed.') },
      { id: 'reports-email-send', label: t('Send by email'), action: t('Generates report and queues email delivery.'), outcome: t('Success message indicates email will arrive; failure shows error message.') },
    ],
    keywords: ['reports', 'pdf', 'email report', 'budget period', 'sections', 'download', 'buttons'],
  },
  {
    id: 'functional-finance-profit-page-buttons',
    family: 'functional',
    section: t('Finance'),
    title: t('Profit reports — complete button catalog'),
    audience: t('Finance/admin users'),
    route: '/workspace/finance/profit',
    objective: t('Explain every control for running profit report by time range and reviewing totals and per-currency breakdown.'),
    steps: [
      t('Set from/to range (optional).'),
      t('Run report and review revenue, COGS, delivery actuals, and net profit.'),
    ],
    buttons: [
      { id: 'profit-run', label: t('Run report'), action: t('Loads profit report for current date-time range.'), outcome: t('Table and KPI cards refresh; API errors appear as banner.') },
    ],
    keywords: ['profit', 'report', 'cogs', 'delivery actuals', 'net profit', 'run report', 'buttons'],
  },
  {
    id: 'functional-finance-delivery-costs-page-buttons',
    family: 'functional',
    section: t('Finance'),
    title: t('Delivery costs — complete button catalog'),
    audience: t('Finance/admin users'),
    route: '/workspace/finance/delivery-costs',
    objective: t('Explain every control for recording delivery actual costs (batch allocations or order-level overrides) and refreshing the audit list.'),
    steps: [
      t('Enter amount (minor), allocation strategy, and either batch id or order id.'),
      t('Save cost and confirm it appears in recent costs list.'),
      t('Refresh list if multiple operators are updating simultaneously.'),
    ],
    buttons: [
      { id: 'delivery-costs-save', label: t('Save cost'), action: t('Creates a delivery actual cost record.'), outcome: t('Cost is stored and affects profit calculations; invalid payload returns API error.') },
      { id: 'delivery-costs-refresh', label: t('Refresh'), action: t('Reloads recent delivery costs list.'), outcome: t('Table reflects latest records from all operators.') },
    ],
    keywords: ['delivery costs', 'actuals', 'allocation strategy', 'batch', 'order override', 'save', 'refresh', 'buttons'],
  },
  {
    id: 'functional-analytics-dashboard-page-buttons',
    family: 'functional',
    section: t('Reports'),
    title: t('Analytics dashboard — complete button catalog'),
    audience: t('Admins/operators with analytics access'),
    route: '/workspace/reports/analytics',
    objective: t('Explain every control for choosing analytics date range and refreshing KPI + chart datasets.'),
    steps: [
      t('Set from/to dates.'),
      t('Press Refresh to reload KPIs and chart series.'),
    ],
    buttons: [
      { id: 'analytics-refresh', label: t('Refresh'), action: t('Loads overview analytics for selected date range.'), outcome: t('Charts and KPI cards update; forbidden/failed loads show error banner.') },
    ],
    keywords: ['analytics', 'dashboard', 'kpis', 'charts', 'date range', 'refresh', 'buttons'],
  },
  {
    id: 'functional-feedback-inbox-page-buttons',
    family: 'functional',
    section: t('Orders'),
    title: t('Feedback inbox — complete button catalog'),
    audience: t('Support/operators handling feedback and complaints'),
    route: '/workspace/feedback-inbox',
    objective: t('Explain every control for filtering feedback/complaints and transitioning ticket status with optional resolution notes.'),
    steps: [
      t('Filter by scope (Active/Archived/All), type, status, and search query.'),
      t('For active items, add optional resolution note and mark status transitions.'),
      t('Archived items are read-only and display resolution note if present.'),
    ],
    buttons: [
      { id: 'feedback-filter-scope', label: t('Scope (select)'), action: t('Switches between Active/Archived/All scopes and resets status filter when scope changes.'), outcome: t('Results reflect only the appropriate lifecycle stage; invalid combinations are avoided.') },
      { id: 'feedback-filter-type', label: t('Type (select)'), action: t('Filters between Feedback vs Complaint.'), outcome: t('Inbox list narrows to selected type.') },
      { id: 'feedback-filter-status', label: t('Status (select)'), action: t('Filters by ticket status.'), outcome: t('Rows narrow; some statuses are hidden depending on scope.') },
      { id: 'feedback-mark-status', label: t('Mark {STATUS}'), action: t('Updates ticket status and persists optional resolution note.'), outcome: t('Row transitions to archived when resolved/rejected; failures show API error message.') },
    ],
    keywords: ['feedback inbox', 'complaint', 'status', 'resolution note', 'filters', 'archived readonly', 'buttons'],
  },
  {
    id: 'functional-purchase-orders-page-buttons',
    family: 'functional',
    section: t('Products'),
    title: t('Purchase orders — complete button catalog'),
    audience: t('Procurement/operators with purchase order access'),
    route: '/workspace/purchase-orders',
    objective: t('Explain every control for creating/editing purchase orders, printing, viewing details, and completing orders with received quantities and final prices.'),
    steps: [
      t('Use search/date/status filters and Search button to load matching orders.'),
      t('Create new purchase order: select supplier, add product lines, set qty/unit/price, then save.'),
      t('Use View to open detail panel; Print to open PDF; Edit/Complete for pending orders only.'),
      t('Complete order by entering received qty and final price per line (must be > 0) and mark completed.'),
    ],
    buttons: [
      { id: 'po-search', label: t('Search'), action: t('Loads purchase orders with current filters.'), outcome: t('Table refreshes to match filters; errors show message.') },
      { id: 'po-new', label: t('New purchase order'), action: t('Opens the create form and resets draft.'), outcome: t('Operator can draft a new supplier order.') },
      { id: 'po-supplier-pick', label: t('Supplier suggestion row'), action: t('Selects supplier from search results.'), outcome: t('Supplier id is set and suggestions close; required for saving.') },
      { id: 'po-product-pick', label: t('Product suggestion row'), action: t('Adds a new PO line for selected product.'), outcome: t('Line appears in items table; qty/unit/price become editable.') },
      { id: 'po-line-remove', label: t('Remove (line)'), action: t('Removes one draft PO line.'), outcome: t('Line is excluded from save payload.') },
      { id: 'po-cancel-form', label: t('Cancel'), action: t('Closes form and clears draft.'), outcome: t('No purchase order is created/updated.') },
      { id: 'po-save', label: t('Save order / Update order'), action: t('Creates or updates purchase order.'), outcome: t('Order persists; validation failures show per-form errors or API message.') },
      { id: 'po-print', label: t('Print'), action: t('Generates PDF and opens it in a new tab.'), outcome: t('Printable order is available for supplier communication.') },
      { id: 'po-view', label: t('View'), action: t('Opens purchase order detail panel.'), outcome: t('Operator can review lines and variance insight summary.') },
      { id: 'po-edit', label: t('Edit'), action: t('Loads order into form for editing (pending only).'), outcome: t('Lines are editable; supplier id may require re-selection per UI policy.') },
      { id: 'po-complete', label: t('Complete'), action: t('Opens completion form for received qty and final price.'), outcome: t('On submit, order becomes completed and downstream costing can be used for profit.') },
      { id: 'po-complete-submit', label: t('Mark as completed'), action: t('Submits completion payload.'), outcome: t('Order status becomes COMPLETED; invalid lines block submit with clear error.') },
      { id: 'po-pagination-prev', label: t('Prev'), action: t('Loads previous page of purchase orders.'), outcome: t('Navigates backwards through result pages.') },
      { id: 'po-pagination-next', label: t('Next'), action: t('Loads next page of purchase orders.'), outcome: t('Navigates forward through result pages.') },
      { id: 'po-detail-close', label: t('Close'), action: t('Closes purchase order detail panel.'), outcome: t('Returns focus to list.') },
    ],
    keywords: ['purchase orders', 'supplier', 'print pdf', 'complete', 'final price', 'received qty', 'filters', 'pagination', 'buttons'],
  },
  {
    id: 'functional-losses-page-buttons',
    family: 'functional',
    section: t('Products'),
    title: t('Losses — complete button catalog'),
    audience: t('Operators with loss recording permissions'),
    route: '/workspace/losses',
    objective: t('Explain every control for filtering loss history and recording a new loss against a product and optional lot.'),
    steps: [
      t('Filter by reason/from/to dates; clear filters when needed.'),
      t('Open record form, search product, optionally choose a lot, enter qty, reason, and notes.'),
      t('Submit to record loss; it deducts stock and impacts active budget/profit.'),
    ],
    buttons: [
      { id: 'losses-clear-filters', label: t('Clear filters'), action: t('Resets filters and returns to page 1.'), outcome: t('Loss history reloads with default query.') },
      { id: 'losses-toggle-form', label: t('Record loss / Cancel'), action: t('Opens/closes loss recording form.'), outcome: t('Operator can start loss intake or return to history view.') },
      { id: 'losses-pick-product', label: t('Product suggestion row'), action: t('Selects product for loss.'), outcome: t('Product id is set and lot selector loads if lots exist.') },
      { id: 'losses-submit', label: t('Record loss'), action: t('Creates loss event.'), outcome: t('Loss appears in history; invalid qty/product blocks with error or API message.') },
      { id: 'losses-pagination-prev', label: t('Prev'), action: t('Moves to previous page of loss history.'), outcome: t('Shows earlier losses.') },
      { id: 'losses-pagination-next', label: t('Next'), action: t('Moves to next page of loss history.'), outcome: t('Shows later losses.') },
    ],
    keywords: ['losses', 'expired', 'damaged', 'theft', 'filter', 'record', 'lot optional', 'pagination', 'buttons'],
  },
  {
    id: 'functional-suppliers-page-buttons',
    family: 'functional',
    section: t('Users'),
    title: t('Suppliers — complete button catalog'),
    audience: t('Operators with supplier management permissions'),
    route: '/workspace/suppliers',
    objective: t('Explain every control for searching suppliers, creating/updating supplier records, and activating/deactivating suppliers.'),
    steps: [
      t('Use search and “Active only” checkbox to scope list.'),
      t('Create new supplier or edit existing supplier in the form.'),
      t('Activate/deactivate supplier to control whether they can be selected in purchasing flows.'),
    ],
    buttons: [
      { id: 'suppliers-new', label: t('New supplier / Hide form'), action: t('Opens or closes supplier create form.'), outcome: t('Operator can add supplier records inline.') },
      { id: 'suppliers-save', label: t('Save supplier / Update supplier'), action: t('Creates or updates supplier record.'), outcome: t('Supplier persists; errors show API message.') },
      { id: 'suppliers-cancel', label: t('Cancel'), action: t('Closes form and clears draft.'), outcome: t('No changes are applied.') },
      { id: 'suppliers-edit', label: t('Edit'), action: t('Loads supplier into form for editing.'), outcome: t('Fields prefill and save updates the supplier.') },
      { id: 'suppliers-toggle-active', label: t('Activate / Deactivate'), action: t('Toggles supplier active state.'), outcome: t('Inactive suppliers are excluded from “activeOnly” selection in purchase order flows.') },
      { id: 'suppliers-pagination-prev', label: t('Prev'), action: t('Loads previous suppliers page.'), outcome: t('Navigates backwards through supplier list.') },
      { id: 'suppliers-pagination-next', label: t('Next'), action: t('Loads next suppliers page.'), outcome: t('Navigates forward through supplier list.') },
    ],
    keywords: ['suppliers', 'create', 'update', 'activate', 'deactivate', 'search', 'pagination', 'buttons'],
  },
  {
    id: 'functional-customer-detail-page-buttons',
    family: 'functional',
    section: t('Users'),
    title: t('Customer detail — complete button catalog'),
    audience: t('Support/admin users with customer permissions'),
    route: '/workspace/customers/:id',
    objective: t('Explain every control for suspending/re-enabling customers, recomputing routes, and paging within customer order history.'),
    steps: [
      t('Use back link to return to filtered customer list (return query preserved).'),
      t('Suspend/unsuspend customer as needed (permission required).'),
      t('Recompute customer route to refresh driving/abroad delivery estimates (permission required).'),
      t('Page through customer’s orders within the detail view.'),
      t('Use route breakdown toggle for abroad delivery to see leg-by-leg breakdown.'),
    ],
    buttons: [
      { id: 'customer-detail-back', label: t('Back to list'), action: t('Navigates back to customers list preserving return query.'), outcome: t('Support resumes previous filter context without rebuilding it.') },
      { id: 'customer-detail-suspend', label: t('Suspend / Unsuspend'), action: t('Updates customer status to BLOCKED or ACTIVE.'), outcome: t('Blocked customers cannot operate normally; UI shows notice and refreshed profile.') },
      { id: 'customer-detail-recompute-route', label: t('Recompute customer route'), action: t('Queues route recomputation job for this customer.'), outcome: t('Driving/abroad route estimates refresh after job; failures show API message.') },
      { id: 'customer-detail-route-breakdown-toggle', label: t('International route breakdown'), action: t('Expands/collapses abroad route legs breakdown panel.'), outcome: t('Operators can audit routing legs, distances, and ETAs.') },
      { id: 'customer-detail-orders-prev', label: t('Prev'), action: t('Moves to previous orders page inside customer detail.'), outcome: t('Shows earlier orders for this customer.') },
      { id: 'customer-detail-orders-next', label: t('Next'), action: t('Moves to next orders page inside customer detail.'), outcome: t('Shows later orders for this customer.') },
    ],
    keywords: ['customer detail', 'suspend', 'blocked', 'route recompute', 'abroad route', 'orders pagination', 'support', 'buttons'],
  },
  {
    id: 'functional-order-statistics-page-buttons',
    family: 'functional',
    section: t('Orders'),
    title: t('Order statistics — complete button catalog'),
    audience: t('Operators with ORDER_STATS access'),
    route: '/workspace/order-statistics',
    objective: t('Explain every control for filtering order statistics and using status/country chips to drill down.'),
    steps: [
      t('Set search/country/status/date filters; stats update after load.'),
      t('Click status/country chips to apply filters quickly.'),
      t('Use Clear to reset all filters.'),
    ],
    buttons: [
      { id: 'order-stats-clear', label: t('Clear'), action: t('Resets all filters.'), outcome: t('Stats reload to default full-range view.') },
      { id: 'order-stats-status-chip', label: t('Status chip'), action: t('Sets status filter to selected status.'), outcome: t('By-status table narrows to that status.') },
      { id: 'order-stats-country-chip', label: t('Country chip'), action: t('Sets country filter.'), outcome: t('Stats narrow to selected country.') },
      { id: 'order-stats-country-row', label: t('Country performance row'), action: t('Sets country filter to clicked row country.'), outcome: t('Drill-down by country becomes immediate for investigation.') },
    ],
    keywords: ['order statistics', 'filters', 'status', 'country', 'performance', 'clear', 'buttons'],
  },
  {
    id: 'functional-workspace-dashboard-page-buttons',
    family: 'functional',
    section: t('General'),
    title: t('Workspace dashboard — complete button catalog'),
    audience: t('All workspace users'),
    route: '/workspace',
    objective: t('Describe the dashboard landing view cards (informational only in current implementation).'),
    steps: [
      t('Open workspace dashboard to see the landing cards.'),
      t('Use sidebar navigation to access functional modules; dashboard cards are informational placeholders.'),
    ],
    buttons: [],
    keywords: ['dashboard', 'workspace', 'landing', 'cards'],
  },
  {
    id: 'functional-settings',
    family: 'functional',
    section: t('System settings'),
    title: t('Display, site content, recommendations, hero, currencies, delivery pricing, email routing'),
    audience: t('Admins and platform owners'),
    route: '/workspace/display',
    objective: t(
      'Govern storefront behavior, messaging, pricing logic, and operational communications from one controlled area.',
    ),
    steps: [
      t('Tune display settings and editable public content.'),
      t('Configure hero, recommendations, landing promotions, and testimonials.'),
      t('Manage supported currencies for global storefront pricing.'),
      t('Set delivery pricing models and route-computation strategy.'),
      t('Configure email sender routing by transactional purpose.'),
      t('Set expiry alert windows and recipients for stock risk prevention.'),
    ],
    buttons: [
      {
        id: 'delivery-pricing-save',
        label: t('Save delivery pricing'),
        action: t('Persists delivery pricing strategy and rules.'),
        outcome: t('Checkout fee calculations use updated policy.'),
      },
      {
        id: 'mail-routing-save',
        label: t('Save mail routing'),
        action: t('Persists sender/reply-to rules by email purpose.'),
        outcome: t('Transactional emails use approved sender identities.'),
      },
      {
        id: 'currencies-add',
        label: t('Add currency'),
        action: t('Adds supported currency from known list.'),
        outcome: t('New currency becomes selectable in the platform.'),
      },
    ],
    keywords: ['settings', 'hero', 'currencies', 'delivery pricing', 'mail routing', 'expiry alerts'],
  },
  {
    id: 'functional-packages-page-buttons',
    family: 'functional',
    section: t('Products'),
    title: t('Packages — complete button catalog'),
    audience: t('Catalog operators with PRODUCT permissions'),
    route: '/workspace/packages',
    objective: t('Explain every control for creating, updating, activating, and merchandising bundled product packages.'),
    steps: [
      t('Open package form to define package metadata and included products.'),
      t('Set package price/discount behavior and publish visibility settings.'),
      t('Use edit and status actions to maintain existing packages safely.'),
    ],
    buttons: [
      { id: 'packages-create', label: t('Create package'), action: t('Creates a new package bundle.'), outcome: t('Package is saved and appears in package listing/storefront when active.') },
      { id: 'packages-update', label: t('Update package'), action: t('Saves modifications to an existing package.'), outcome: t('Package details refresh and new settings apply immediately.') },
      { id: 'packages-toggle-active', label: t('Activate / Deactivate'), action: t('Toggles package active status.'), outcome: t('Controls storefront visibility and checkout eligibility for the package.') },
    ],
    keywords: ['packages', 'bundle', 'catalog', 'activate', 'deactivate', 'pricing', 'buttons'],
  },
  {
    id: 'functional-referrals-page-buttons',
    family: 'functional',
    section: t('Marketing'),
    title: t('Referrals — complete button catalog'),
    audience: t('Marketing/ops users with REFERRAL permissions'),
    route: '/workspace/referrals',
    objective: t('Explain controls for referral program configuration, eligibility, and operational monitoring.'),
    steps: [
      t('Review referral program settings and current campaign state.'),
      t('Update reward values, qualification rules, and publication state.'),
      t('Track claim/sign-up intent activity and investigate anomalies.'),
    ],
    buttons: [
      { id: 'referrals-save-settings', label: t('Save referral program'), action: t('Persists referral program configuration.'), outcome: t('Updated referral policy applies to new signups and claims.') },
      { id: 'referrals-refresh', label: t('Refresh'), action: t('Reloads referral metrics and entries.'), outcome: t('Operators get up-to-date program performance and queue state.') },
    ],
    keywords: ['referrals', 'invite code', 'reward', 'signup intent', 'claim', 'program settings', 'buttons'],
  },
  {
    id: 'functional-activities-page-buttons',
    family: 'functional',
    section: t('Security and identity'),
    title: t('Activity logs and staff audit — complete button catalog'),
    audience: t('Security/admin users with ACTIVITY permissions'),
    route: '/workspace/activities',
    objective: t('Explain controls for querying workspace activity events and staff action audit trails.'),
    steps: [
      t('Select date range, actor filters, and event categories.'),
      t('Open targeted entries for request metadata and contextual details.'),
      t('Use dedicated staff action audit route for privileged operation traces.'),
    ],
    buttons: [
      { id: 'activities-apply-filters', label: t('Apply filters'), action: t('Runs activity query with selected filters.'), outcome: t('List narrows to the relevant audit window for investigations.') },
      { id: 'activities-reset-filters', label: t('Reset'), action: t('Clears active filters.'), outcome: t('Returns to default activity listing scope.') },
      { id: 'activities-open-entry', label: t('View details'), action: t('Opens detailed payload for one activity event.'), outcome: t('Security/operator can inspect actor, target, and request context.') },
    ],
    keywords: ['activities', 'audit', 'staff action audit', 'security', 'event log', 'filters', 'buttons'],
  },
  {
    id: 'functional-geo-access-page-buttons',
    family: 'functional',
    section: t('System settings'),
    title: t('Geo access control — complete button catalog'),
    audience: t('Admins/operators with UI setting access'),
    route: '/workspace/geo-access',
    objective: t('Explain controls for allowed/blocked country access policy affecting storefront entry.'),
    steps: [
      t('Choose policy mode (allow-list or block-list) based on compliance model.'),
      t('Add/remove country codes and save policy.'),
      t('Validate storefront behavior from allowed and blocked geographies.'),
    ],
    buttons: [
      { id: 'geo-access-add-country', label: t('Add country'), action: t('Adds country to selected policy list.'), outcome: t('Country is included in allow/block evaluation for incoming users.') },
      { id: 'geo-access-remove-country', label: t('Remove'), action: t('Removes country from policy list.'), outcome: t('Country no longer participates in explicit access override logic.') },
      { id: 'geo-access-save', label: t('Save'), action: t('Persists geo access configuration.'), outcome: t('Geo guard starts enforcing updated policy immediately.') },
    ],
    keywords: ['geo access', 'country allowlist', 'country blocklist', 'compliance', 'save', 'buttons'],
  },
  {
    id: 'functional-account-deletion-retention-page-buttons',
    family: 'functional',
    section: t('Users'),
    title: t('Account deletion retention — complete button catalog'),
    audience: t('Admins/operators with UI setting access'),
    route: '/workspace/account-deletion-retention',
    objective: t('Explain controls for account deletion retention offer behavior and cooldown policy.'),
    steps: [
      t('Review retention offer copy, discount/benefit policy, and cooldown parameters.'),
      t('Enable/disable the retention offer flow and update limits.'),
      t('Save settings and verify deletion flow uses the new policy.'),
    ],
    buttons: [
      { id: 'account-deletion-retention-save', label: t('Save settings'), action: t('Persists account deletion retention configuration.'), outcome: t('Customer deletion flow applies updated retention and cooldown rules.') },
      { id: 'account-deletion-retention-toggle', label: t('Enable retention offer'), action: t('Turns retention offer behavior on or off.'), outcome: t('Deletion flow either presents offer or skips directly to deletion.') },
    ],
    keywords: ['account deletion', 'retention offer', 'cooldown', 'customer lifecycle', 'settings', 'buttons'],
  },
  {
    id: 'functional-documentation-pages',
    family: 'functional',
    section: t('General'),
    title: t('Workspace documentation pages'),
    audience: t('All workspace users'),
    route: '/workspace/docs',
    objective: t('Explain how functional and technical documentation are accessed and searched directly inside workspace.'),
    steps: [
      t('Open Documentation from sidebar Overview section.'),
      t('Use search input to filter articles by section, objective, and keywords.'),
      t('Open Technical documentation for architecture, API, and security model topics.'),
    ],
    buttons: [
      { id: 'docs-open-article', label: t('Open article'), action: t('Opens selected documentation article in the content pane.'), outcome: t('Operator can follow procedures and button reference details for the target module.') },
    ],
    keywords: ['documentation', 'functional docs', 'technical docs', 'search', 'knowledge base', 'buttons'],
  },
  {
    id: 'functional-customer-inactivity-lifecycle',
    family: 'functional',
    section: t('Users'),
    title: t('Customer inactivity lifecycle and admin-approved deletion'),
    audience: t('Admins/operators with customer update permissions'),
    route: '/workspace/customers',
    objective: t('Explain how inactivity warnings, blocking, and admin deletion approval are managed.'),
    steps: [
      t('Configure inactivity threshold and warning cadence in Account deletion settings.'),
      t('Review customer profile activity badge and lifecycle status on customer detail page.'),
      t('Approve or reject pending inactivity deletion requests from customer detail.'),
    ],
    buttons: [
      { id: 'customer-inactivity-approve', label: t('Approve inactivity deletion'), action: t('Approves a blocked/pending customer for archive deletion.'), outcome: t('Account is archived and removed from active users while preserving compliance traces.') },
      { id: 'customer-inactivity-reject', label: t('Keep account active'), action: t('Rejects pending inactivity deletion and re-enables the account.'), outcome: t('Customer returns to active state and inactivity lifecycle counters reset.') },
      { id: 'customer-inactivity-policy-save', label: t('Save'), action: t('Persists inactivity threshold/warning/admin notification policy.'), outcome: t('Scheduler uses updated policy for warning, blocking, and admin review flow.') },
    ],
    keywords: ['inactivity', 'account lifecycle', 'warnings', 'blocked account', 'admin approval', 'archive deletion'],
  },
  {
    id: 'functional-security',
    family: 'functional',
    section: t('Security and identity'),
    title: t('Password, MFA, passkeys, recovery codes, account recovery'),
    audience: t('All authenticated users'),
    route: '/workspace/profile',
    objective: t(
      'Ensure strong identity proofing while maintaining business continuity through secure recovery controls.',
    ),
    steps: [
      t('Employees/admins authenticate with email/password plus MFA code verification.'),
      t('Users can enroll passkeys per device and manage passkey labels/devices.'),
      t('Sensitive actions require recent session or passkey re-authentication.'),
      t('Recovery codes can be regenerated with current password and policy checks.'),
      t('Account recovery supports code-based flow and can require passkey proof when passkeys exist.'),
      t('Email remains critical: changed recovery email is re-verified before trust is restored.'),
    ],
    buttons: [
      {
        id: 'passkey-add-device',
        label: t('Add this device as passkey'),
        action: t('Registers a passkey credential for current account.'),
        outcome: t('User can sign in with passkey from registered authenticator.'),
      },
      {
        id: 'passkey-rename',
        label: t('Rename'),
        action: t('Updates passkey nickname.'),
        outcome: t('Credential inventory is easier to audit by humans.'),
      },
      {
        id: 'passkey-delete',
        label: t('Delete'),
        action: t('Removes selected passkey after policy checks.'),
        outcome: t('Compromised/lost authenticator can be revoked.'),
      },
      {
        id: 'recovery-generate',
        label: t('Generate recovery codes'),
        action: t('Creates new one-time recovery set and invalidates old set.'),
        outcome: t('User retains emergency access path with controlled rotation.'),
      },
    ],
    keywords: ['mfa', 'passkey', 'recovery', 'email verification', 'security'],
  },
  {
    id: 'technical-frontend-architecture',
    family: 'technical',
    section: t('Frontend architecture'),
    title: t('React workspace shell and page composition'),
    audience: t('Frontend engineers'),
    objective: t(
      'Document how workspace routes, permission gates, layout shell, and feature modules compose the staff experience.',
    ),
    steps: [
      t('Routes are declared centrally and guarded by `RequireWorkspaceAuth` / permission wrappers.'),
      t('`MainLayout` handles sidebar rendering, role-aware visibility, and workspace search index.'),
      t('Feature pages live in domain folders and consume `AuthContext.authorizedFetch` for protected APIs.'),
      t('Localization is managed with i18next modules and can be expanded for documentation content.'),
    ],
    buttons: [],
    keywords: ['react', 'routes', 'layout', 'permissions', 'i18n', 'frontend'],
  },
  {
    id: 'technical-backend-architecture',
    family: 'technical',
    section: t('Backend architecture'),
    title: t('NestJS modules, Prisma data layer, and auth orchestration'),
    audience: t('Backend engineers'),
    objective: t(
      'Explain service boundaries and the main request lifecycle from controller to persistence.',
    ),
    steps: [
      t('NestJS modules encapsulate business domains (IAM, sales, finance, settings, etc.).'),
      t('Controllers validate DTO payloads and delegate to services for business logic.'),
      t('Prisma is the typed persistence layer with relational models and constraints.'),
      t('Auth service coordinates credential checks, MFA, passkeys, sessions, and token issuance.'),
    ],
    buttons: [],
    keywords: ['nestjs', 'prisma', 'service layer', 'controller', 'backend'],
  },
  {
    id: 'technical-auth-sessions',
    family: 'technical',
    section: t('Security model'),
    title: t('Tokens, sessions, idle strategy, and revocation'),
    audience: t('Security and platform engineers'),
    objective: t(
      'Define the practical security model and where enforcement occurs between frontend and backend.',
    ),
    steps: [
      t('Frontend idle timers improve UX but backend token/session validation is the security authority.'),
      t('Access tokens are short-lived; refresh flow validates session ownership and freshness.'),
      t('Session records enforce expiry/revocation and are updated with activity during refresh.'),
      t('Logout revokes server-side session; blocked accounts fail token refresh and auth operations.'),
    ],
    buttons: [],
    keywords: ['jwt', 'session', 'refresh', 'idle timeout', 'revocation', 'security'],
  },
  {
    id: 'technical-passkeys',
    family: 'technical',
    section: t('Security model'),
    title: t('WebAuthn passkey lifecycle'),
    audience: t('Security and backend engineers'),
    objective: t(
      'Describe registration, authentication, credential management, and sensitive-action re-auth flow.',
    ),
    steps: [
      t('Server generates registration/authentication options and signs challenge tokens.'),
      t('Client performs browser WebAuthn ceremony using server-provided options.'),
      t('Server verifies response against expected challenge, RP ID, and origin.'),
      t('Credential metadata and counters are stored and updated to prevent replay abuse.'),
      t('Sensitive operations can require passkey re-auth if recent session proof is insufficient.'),
    ],
    buttons: [],
    keywords: ['webauthn', 'passkey', 'challenge', 'rp id', 'origin', 'counter'],
  },
  {
    id: 'technical-email-verification',
    family: 'technical',
    section: t('Identity assurance'),
    title: t('Email verification as trust anchor'),
    audience: t('Security and product engineering'),
    objective: t(
      'Keep email as mandatory trust channel for account legitimacy, transactional comms, and recovery continuity.',
    ),
    steps: [
      t('Customer sign-up issues verification token; unverified users are prompted to verify before full use.'),
      t('Transactional security emails (MFA, password reset, invite flows) depend on valid mailbox ownership.'),
      t('Recovery flows that change email reset verification state and dispatch a new verification link.'),
      t('No sign-in method (password or passkey) removes email importance for trust and notifications.'),
    ],
    buttons: [],
    keywords: ['email', 'verification', 'identity', 'mfa', 'recovery'],
  },
  {
    id: 'technical-rbac',
    family: 'technical',
    section: t('Authorization'),
    title: t('Role-based access control and permission mapping'),
    audience: t('Admins and engineers'),
    objective: t(
      'Describe how roles and permission keys gate API endpoints and workspace UI exposure.',
    ),
    steps: [
      t('Permission catalog defines resource/action pairs for each domain.'),
      t('Roles aggregate permissions and can be assigned to users.'),
      t('Backend guards enforce permission checks on protected endpoints.'),
      t('Frontend sidebar and route wrappers hide inaccessible modules to reduce operator error.'),
    ],
    buttons: [],
    keywords: ['rbac', 'permissions', 'roles', 'guards', 'authorization'],
  },
  {
    id: 'technical-observability',
    family: 'technical',
    section: t('Operations'),
    title: t('Monitoring, background jobs, and failure handling'),
    audience: t('Platform and SRE teams'),
    objective: t(
      'Explain runtime concerns for async jobs, notification pipelines, and operational stability.',
    ),
    steps: [
      t('Long-running tasks (e.g., delivery dispatch, route recomputation) are queued and stateful.'),
      t('Job status is persisted for UI polling and post-failure diagnostics.'),
      t('Error paths return safe user-facing messages while preserving backend traceability.'),
      t('Operational runbooks should include Redis health, mail provider health, and queue backlog checks.'),
    ],
    buttons: [],
    keywords: ['jobs', 'queue', 'redis', 'monitoring', 'operations', 'failure'],
  },
  {
    id: 'technical-api-contracts',
    family: 'technical',
    section: t('API'),
    title: t('Endpoint contract standards and DTO validation'),
    audience: t('Backend and frontend engineers'),
    objective: t(
      'Standardize request/response patterns and validation strategy to reduce integration drift.',
    ),
    steps: [
      t('Use DTO classes with validation decorators for all external request payloads.'),
      t('Prefer explicit, minimal response payloads with stable field names.'),
      t('Use throttling on sensitive auth and recovery endpoints to reduce abuse risk.'),
      t('Keep unauthorized/forbidden errors consistent for frontend handling and UX messaging.'),
    ],
    buttons: [],
    keywords: ['dto', 'validation', 'throttle', 'contract', 'api'],
  },
];

export const getLocalized = (value: LocalizedText, locale: string) => {
  const normalized = (locale || 'en').slice(0, 2) as DocLocale;
  return value[normalized] || value.en;
};
